import {useState} from "react";
import {Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, Divider, IconButton, MenuItem, Stack, Switch, TextField, Tooltip, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {appStyles} from "../../../AppStyles.ts";
import type {GraphSettings, GraphRootNode} from "../../../sdo/GraphSettings.ts";

// TODO: Replace with full ForceLayout enum from desktop (GraphViewerUtils.resolveLayout → ForceLayout.values()).
// Desktop default is GRAVITY. The others below are placeholders until the full list is supplied.
export const LAYOUT_OPTIONS = ["GRAVITY", "FORCE", "RADIAL", "CIRCULAR", "HIERARCHICAL"];
export const EDGE_STROKE_STYLES = ["SOLID", "DASHED", "DOTTED"];

// --- Form styles ---

const formFieldSx = {
    backgroundColor: "rgba(9, 25, 32, 0.6)",
    borderRadius: "6px",
    "& .MuiOutlinedInput-root": {
        borderRadius: "6px",
        color: "#f2f5fa",
        fontSize: "0.8rem",
        "& fieldset": {border: "1px solid rgba(112, 147, 160, 0.35)"},
        "&:hover fieldset": {border: "1px solid #02cfee"},
        "&.Mui-focused fieldset": {border: "1px solid #38ff82", boxShadow: "0 0 6px rgba(56, 255, 130, 0.2)"},
    },
    "& .MuiInputLabel-root": {color: "rgba(242, 245, 250, 0.55)", fontSize: "0.8rem"},
    "& .MuiInputLabel-root.Mui-focused": {color: "#38ff82"},
};

const sectionTitleSx = {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#a8b8c0",
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
    textAlign: "left" as const,
};

const fieldLabelSx = {
    fontSize: "0.7rem",
    color: "#a8b8c0",
    textAlign: "left" as const,
    minWidth: 140,
};

const subgroupSx = {
    border: "1px solid rgba(112, 147, 160, 0.25)",
    borderRadius: "6px",
    padding: "8px 12px",
};

const subgroupTitleSx = {
    fontSize: "0.65rem",
    fontWeight: 700,
    color: "#7093a0",
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
    textAlign: "left" as const,
    marginBottom: "6px",
};

function SwitchRow({label, checked, onChange}: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={fieldLabelSx}>{label}</Typography>
            <Switch
                size="small"
                checked={checked}
                onChange={e => onChange(e.target.checked)}
                sx={{"& .Mui-checked": {color: "#38ff82"}, "& .Mui-checked + .MuiSwitch-track": {backgroundColor: "#38ff82"}}}
            />
        </Stack>
    );
}

function ColorRow({label, enabled, onEnabledChange, color, onColorChange}: {
    label: string; enabled: boolean; onEnabledChange: (v: boolean) => void;
    color: string; onColorChange: (v: string) => void;
}) {
    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={fieldLabelSx}>{label}</Typography>
            <Switch
                size="small"
                checked={enabled}
                onChange={e => onEnabledChange(e.target.checked)}
                sx={{"& .Mui-checked": {color: "#38ff82"}, "& .Mui-checked + .MuiSwitch-track": {backgroundColor: "#38ff82"}}}
            />
            <Box
                component="input"
                type="color"
                value={color}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onColorChange(e.target.value)}
                disabled={!enabled}
                sx={{
                    width: 40, height: 28, padding: 0, border: "1px solid rgba(112, 147, 160, 0.35)",
                    borderRadius: "4px", backgroundColor: "transparent", cursor: enabled ? "pointer" : "not-allowed",
                    opacity: enabled ? 1 : 0.35,
                }}
            />
            <Typography sx={{fontSize: "0.7rem", color: enabled ? "#F2F5FA" : "#7093a0", fontFamily: "monospace"}}>{color.toUpperCase()}</Typography>
        </Stack>
    );
}

function CollapsibleSection({title, open, onToggle, children}: {
    title: string; open: boolean; onToggle: () => void; children: React.ReactNode;
}) {
    return (
        <Stack direction="column" spacing={1}>
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={onToggle}
                sx={{
                    borderBottom: "1px solid #a8b8c0",
                    pb: 0.5,
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": {opacity: 0.85},
                }}
            >
                {open
                    ? <ExpandMoreIcon sx={{fontSize: 20, color: "#a8b8c0"}} />
                    : <ChevronRightIcon sx={{fontSize: 20, color: "#a8b8c0"}} />}
                <Typography sx={sectionTitleSx}>{title}</Typography>
            </Stack>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{paddingLeft: 2, paddingTop: 1}}>{children}</Box>
            </Collapse>
        </Stack>
    );
}

export interface GraphEditFormProps {
    initial: GraphSettings;
    onSave: (graph: GraphSettings) => void;
    onCancel: () => void;
    onDelete: () => void;
}

type SectionKey = "colors" | "font" | "edge";

const ALL_SECTIONS: SectionKey[] = ["colors", "font", "edge"];

export default function GraphEditForm({initial, onSave, onCancel, onDelete}: GraphEditFormProps) {
    const [draft, setDraft] = useState<GraphSettings>(initial);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [openSections, setOpenSections] = useState<Set<SectionKey>>(() => new Set(ALL_SECTIONS));
    const [addRootOpen, setAddRootOpen] = useState(false);

    function update<K extends keyof GraphSettings>(key: K, value: GraphSettings[K]) {
        setDraft(prev => ({...prev, [key]: value}));
    }

    function removeRootNode(uid: string) {
        setDraft(prev => ({...prev, rootNodes: prev.rootNodes.filter(n => n.uid !== uid)}));
    }

    function toggleSection(key: SectionKey) {
        setOpenSections(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }

    function toggleAddRoot() {
        setAddRootOpen(prev => {
            const next = !prev;
            if (next) setOpenSections(new Set()); // collapse the 3 sections below
            else setOpenSections(new Set(ALL_SECTIONS)); // restore default expanded state
            return next;
        });
    }

    function handleSave() {
        if (!draft.title.trim()) return;
        onSave({...draft, title: draft.title.trim()});
    }

    return (
        <Box sx={{
            width: "100%",
            maxWidth: 720,
            maxHeight: "85vh",
            overflowY: "auto",
            background: "linear-gradient(135deg, rgba(9,25,32,0.95) 0%, rgba(20,40,55,0.85) 100%)",
            border: "1px solid rgba(2, 207, 238, 0.35)",
            borderRadius: "12px",
            padding: "16px 20px",
        }}>
            <Stack spacing={2}>
                {/* Form title */}
                <Typography sx={{fontSize: "1rem", fontWeight: 700, color: "#F2F5FA", textAlign: "left"}}>
                    Graph Settings
                </Typography>

                <Divider sx={{borderColor: "#3F464F"}} />

                {/* Basics */}
                <Stack spacing={1}>
                    <Typography sx={sectionTitleSx}>Details</Typography>
                    <TextField
                        size="small"
                        label="Title"
                        value={draft.title}
                        onChange={e => update("title", e.target.value)}
                        sx={formFieldSx}
                        fullWidth
                    />
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <TextField
                            size="small"
                            select
                            label="Layout"
                            value={draft.layout}
                            onChange={e => update("layout", e.target.value)}
                            sx={{...formFieldSx, minWidth: 180}}
                        >
                            {LAYOUT_OPTIONS.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </TextField>
                        <SwitchRow label="Save Node Positions" checked={draft.saveNodePositions} onChange={v => update("saveNodePositions", v)} />
                    </Stack>
                </Stack>

                <Divider sx={{borderColor: "#3F464F"}} />

                {/* Root Nodes — never collapsed */}
                <Stack spacing={1}>
                    <Typography sx={sectionTitleSx}>Root Nodes ({draft.rootNodes.length})</Typography>
                    <Stack spacing={0.5}>
                        {draft.rootNodes.map((node: GraphRootNode) => (
                            <Stack key={node.uid} direction="row" alignItems="center" spacing={1} sx={{
                                background: "rgba(0, 71, 106, 0.3)",
                                borderRadius: "6px",
                                padding: "6px 8px",
                            }}>
                                <Stack spacing={0} sx={{flex: 1, minWidth: 0}}>
                                    <Typography sx={{fontSize: "0.75rem", color: "#F2F5FA", textAlign: "left"}}>{node.display}</Typography>
                                    <Typography sx={{fontSize: "0.6rem", color: "#a8b8c0", textAlign: "left"}}>{node.profileType}: {node.typeKey}</Typography>
                                </Stack>
                                <Tooltip title="Remove root node">
                                    <IconButton size="small" onClick={() => removeRootNode(node.uid)} sx={{color: "#7093a0", "&:hover": {color: "#d83f2f"}}}>
                                        <CloseIcon sx={{fontSize: 16}} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        ))}
                        {draft.rootNodes.length === 0 && (
                            <Typography sx={{fontSize: "0.7rem", color: "#a8b8c0", fontStyle: "italic", textAlign: "left"}}>No root nodes.</Typography>
                        )}
                    </Stack>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={addRootOpen ? <ExpandMoreIcon sx={{fontSize: 16}} /> : <AddIcon sx={{fontSize: 14}} />}
                        onClick={toggleAddRoot}
                        sx={{
                            fontSize: "0.7rem", textTransform: "none", alignSelf: "flex-start",
                            borderColor: addRootOpen ? "#02cfee" : "#7093a0",
                            color: addRootOpen ? "#02cfee" : "#a8b8c0",
                            "&:hover": {borderColor: "#02cfee", color: "#02cfee"},
                        }}
                    >
                        {addRootOpen ? "Close Add Root Node" : "Add Root Node"}
                    </Button>
                    <Collapse in={addRootOpen} timeout="auto" unmountOnExit>
                        <Box sx={{
                            border: "1px dashed rgba(2, 207, 238, 0.4)",
                            borderRadius: "6px",
                            padding: "12px",
                            marginTop: "8px",
                        }}>
                            {/* TODO: Root node picker — search intel/resource profiles, select, dispatch to setDraft(prev.rootNodes + new). */}
                            <Typography sx={{fontSize: "0.75rem", color: "#a8b8c0", fontStyle: "italic", textAlign: "center"}}>
                                Root node picker not yet implemented.
                            </Typography>
                            <Typography sx={{fontSize: "0.65rem", color: "#7093a0", textAlign: "center", mt: 0.5}}>
                                Search + select intel / resource profiles will live here.
                            </Typography>
                        </Box>
                    </Collapse>
                </Stack>

                <Divider sx={{borderColor: "#3F464F"}} />

                {/* Collapsible sections — only one open at a time; closed when addRoot is open */}

                {/* Node Colors */}
                <CollapsibleSection title="Node Colors" open={openSections.has("colors") && !addRootOpen} onToggle={() => toggleSection("colors")}>
                    <Stack spacing={1}>
                        <ColorRow label="Root Node" enabled={draft.rootNodeColorEnabled} onEnabledChange={v => update("rootNodeColorEnabled", v)} color={draft.rootNodeColorHex} onColorChange={v => update("rootNodeColorHex", v)} />
                        <ColorRow label="Resource Node" enabled={draft.resourceNodeColorEnabled} onEnabledChange={v => update("resourceNodeColorEnabled", v)} color={draft.resourceNodeColorHex} onColorChange={v => update("resourceNodeColorHex", v)} />
                        <ColorRow label="Intel Node" enabled={draft.intelNodeColorEnabled} onEnabledChange={v => update("intelNodeColorEnabled", v)} color={draft.intelNodeColorHex} onColorChange={v => update("intelNodeColorHex", v)} />
                    </Stack>
                </CollapsibleSection>

                {/* Font Overrides */}
                <CollapsibleSection title="Font Overrides" open={openSections.has("font") && !addRootOpen} onToggle={() => toggleSection("font")}>
                    <Stack spacing={1}>
                        <SwitchRow label="Enable Font Overrides" checked={draft.fontOverrideEnabled} onChange={v => update("fontOverrideEnabled", v)} />
                        {draft.fontOverrideEnabled && (
                            <Stack spacing={1} sx={{paddingLeft: 2}}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <TextField
                                        size="small"
                                        label="Font Family"
                                        value={draft.fontFamily}
                                        onChange={e => update("fontFamily", e.target.value)}
                                        sx={{...formFieldSx, flex: 1}}
                                    />
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Size"
                                        value={draft.fontSize}
                                        onChange={e => update("fontSize", Number(e.target.value))}
                                        sx={{...formFieldSx, width: 90}}
                                        inputProps={{min: 6, max: 48}}
                                    />
                                </Stack>
                                {/* Font Style sub-group — keeps bold/italic/underline visually contained */}
                                <Box sx={subgroupSx}>
                                    <Typography sx={subgroupTitleSx}>Font Style</Typography>
                                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                        <SwitchRow label="Bold" checked={draft.fontBold} onChange={v => update("fontBold", v)} />
                                        <SwitchRow label="Italic" checked={draft.fontItalic} onChange={v => update("fontItalic", v)} />
                                        <SwitchRow label="Underline" checked={draft.fontUnderline} onChange={v => update("fontUnderline", v)} />
                                    </Stack>
                                </Box>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography sx={fieldLabelSx}>Font Color</Typography>
                                    <Box
                                        component="input"
                                        type="color"
                                        value={draft.fontColorHex}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => update("fontColorHex", e.target.value)}
                                        sx={{width: 40, height: 28, padding: 0, border: "1px solid rgba(112, 147, 160, 0.35)", borderRadius: "4px", backgroundColor: "transparent", cursor: "pointer"}}
                                    />
                                    <Typography sx={{fontSize: "0.7rem", color: "#F2F5FA", fontFamily: "monospace"}}>{draft.fontColorHex.toUpperCase()}</Typography>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </CollapsibleSection>

                {/* Edge Style */}
                <CollapsibleSection title="Edge Style" open={openSections.has("edge") && !addRootOpen} onToggle={() => toggleSection("edge")}>
                    <Stack spacing={1}>
                        <SwitchRow label="Enable Edge Overrides" checked={draft.edgeStyleEnabled} onChange={v => update("edgeStyleEnabled", v)} />
                        {draft.edgeStyleEnabled && (
                            <Stack spacing={1} sx={{paddingLeft: 2}}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography sx={fieldLabelSx}>Edge Color</Typography>
                                    <Box
                                        component="input"
                                        type="color"
                                        value={draft.edgeColorHex}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => update("edgeColorHex", e.target.value)}
                                        sx={{width: 40, height: 28, padding: 0, border: "1px solid rgba(112, 147, 160, 0.35)", borderRadius: "4px", backgroundColor: "transparent", cursor: "pointer"}}
                                    />
                                    <Typography sx={{fontSize: "0.7rem", color: "#F2F5FA", fontFamily: "monospace"}}>{draft.edgeColorHex.toUpperCase()}</Typography>
                                </Stack>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Stroke Width"
                                        value={draft.edgeStrokeWidth}
                                        onChange={e => update("edgeStrokeWidth", Number(e.target.value))}
                                        sx={{...formFieldSx, width: 120}}
                                        inputProps={{min: 0.5, max: 10, step: 0.5}}
                                    />
                                    <TextField
                                        size="small"
                                        select
                                        label="Stroke Style"
                                        value={draft.edgeStrokeStyle}
                                        onChange={e => update("edgeStrokeStyle", e.target.value)}
                                        sx={{...formFieldSx, minWidth: 140}}
                                    >
                                        {EDGE_STROKE_STYLES.map(opt => (
                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </CollapsibleSection>

                <Divider sx={{borderColor: "#3F464F"}} />

                {/* Action row: Delete left, Cancel/Save right */}
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                    <Tooltip title="Delete graph">
                        <IconButton
                            size="small"
                            onClick={() => setConfirmDelete(true)}
                            sx={{
                                padding: "4px",
                                color: "#7093a0",
                                "&:hover": {color: "#d83f2f"},
                            }}
                        >
                            <DeleteIcon sx={{fontSize: 18}} />
                        </IconButton>
                    </Tooltip>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onCancel}
                            startIcon={<CloseIcon sx={{fontSize: 14}} />}
                            sx={{
                                fontSize: "0.7rem", textTransform: "none",
                                borderColor: "#7093a0", color: "#a8b8c0",
                                "&:hover": {borderColor: "#d06440", color: "#d06440"},
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleSave}
                            disabled={!draft.title.trim()}
                            sx={{
                                ...appStyles.panelActionButton,
                                "&.Mui-disabled": {
                                    backgroundColor: "rgba(2, 207, 238, 0.15)",
                                    color: "rgba(2, 207, 238, 0.4)",
                                },
                            }}
                        >
                            Save Changes
                        </Button>
                    </Stack>
                </Stack>
            </Stack>

            {/* Delete confirmation dialog */}
            <Dialog
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                slotProps={{paper: {sx: {backgroundColor: "#1a2730", border: "1px solid #3F464F", borderRadius: "12px"}}}}
            >
                <DialogContent>
                    <DialogContentText sx={{color: "#f2f5fa", fontSize: "0.90rem"}}>
                        Are you sure you want to delete this graph?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{padding: "8px 16px 12px"}}>
                    <Button onClick={() => setConfirmDelete(false)} sx={{color: "#7093a0", fontSize: "0.80rem"}}>Cancel</Button>
                    <Button onClick={() => { setConfirmDelete(false); onDelete(); }} sx={{color: "#d83f2f", fontSize: "0.80rem"}}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
